import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { capitalize } from 'lodash';
import React, { useState } from 'react';
import { EngagementPlan } from '@redux/tasks/tasksTypes';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { WarningCircleIcon } from 'components/Icons/WarningCircleIcon';
import { Text } from 'components/Typography';
import { SVGBasicType } from 'types/global';
import { Color } from 'utils/color';
import { customDateTimePickerStyles } from '../../../../customStyles';
import style from './StartPlanModal.scss';

interface StartPlanModalProps {
  open: boolean;
  onClose: () => void;
  engagementPlan: EngagementPlan | null;
  onConfirm: (startDate: Date) => Promise<void>;
  icon?: React.FC<SVGBasicType>;
  title?: string;
  description?: string;
  proceedEventText?: string;
  closeEventText?: string;
}

export const StartPlanModal: React.FC<StartPlanModalProps> = ({
  open,
  onClose,
  engagementPlan,
  onConfirm,
  icon = WarningCircleIcon,
  title,
  description,
  proceedEventText = 'Start Plan',
  closeEventText = 'Cancel'
}) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(tomorrow);
  const [isLoading, setIsLoading] = useState(false);

  const handleStartPlan = async () => {
    if (!selectedDate || !engagementPlan) return;

    setIsLoading(true);
    try {
      await onConfirm(selectedDate);
      onClose();
    } catch (error) {
      console.error('Failed to start plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const planTitle = capitalize(engagementPlan?.title.trim());
  const defaultTitle = title || `Ready to begin ${planTitle}?`;
  const defaultDescription = description || `You're about to start your personalized ${planTitle}, which includes ${engagementPlan?.taskCount} tasks.`;

  if (!engagementPlan) return null;

  return (
    <ConfirmationDialogV2
      open={open}
      icon={icon}
      title={defaultTitle}
      description={defaultDescription}
      proceedEvent={handleStartPlan}
      proceedEventText={proceedEventText}
      closeEvent={onClose}
      closeEventText={closeEventText}
      proceedDisabled={!selectedDate}
      proceedLoading={isLoading}
      iconColor={Color.DARK_BLUE_500}
      iconBackgroundColor={Color.DARK_BLUE_50}
    >
      <div className={style.dateSection}>
        <Text variant="md" weight={500} className={style.dateLabel}>
          Choose your start date
        </Text>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={selectedDate}
            onChange={(newValue) => {
              //time should be at least tomorrow
              if (newValue && newValue < tomorrow) {
                setSelectedDate(tomorrow);
                return;
              }
              setSelectedDate(newValue);
            }}
            minDate={tomorrow}
            className={style.datePicker}
            disabled={isLoading}
            sx={customDateTimePickerStyles}
          />
        </LocalizationProvider>
      </div>
    </ConfirmationDialogV2>
  );
};
